// Shared "Design A · Corporate Band" presentational component for the
// on-screen Print Preview. Mirrors
// backend/src/pdfTemplates/designA/designACore.js's renderDesignABody()
// structure/markup exactly (same config shape, same pagination function —
// see ../../lib/designAPagination.js) so Print Preview is visually
// identical to the downloaded PDF. One voucher-type wrapper per voucher
// type (e.g. SalesVoucherBodyDesignA.jsx) builds the config and renders
// this component -- nothing voucher-type-specific lives here.
import React from "react";
import { paginateDesignARows, DEFAULT_ROW_BUDGETS } from "../../lib/designAPagination";
import "./VoucherBodyDesignA.css";

// config shape (mirrors designACore.js's comment):
// {
//   docTitle, docNumber,
//   institution: { logoUrl, companyName, companyGstin },
//   party: {
//     billToLabel, billTo: { name, lines: [node...] },
//     shipTo: { lines: [node...] } | null,    // only when the wrapper supports it + showShipping is on
//     fromLabel, from: { name, lines: [node...] },
//     metaLabel, meta: [{ label, value }],
//   },
//   columns: [{ label, align }],
//   rows: [{ cells: [node...], isBonus, rawAmount }],
//   totalItems, totalQty,
//   totalsRows: [{ label, value, credit }],
//   grandTotalLabel, grandTotalValue, paidValue, dueValue,
//   amountInWordsText,
//   bankDetails: { bankName, accountNameAsPerBank, accountNo, branch, ifsc, swiftCode, ibanNumber } | null,
//     (from the Chart of Accounts bank account marked "primary" -- see
//     apiservice/accounts.js's getPrimaryBankAccountApi -- never typed separately)
//   taxSummary: { rows: [...], mode } | null,
//   terms,
//   signatory: { companyName, stampUrl, signatureUrl },
//   bonusNote: string | null,
//   rowBudgets: { single, first, middle, last } (optional),
// }

function HeaderBand({ config, slim }) {
  const { institution = {}, docTitle, docNumber } = config;
  const logo = institution.logoUrl ? (
    <img src={institution.logoUrl} alt={institution.companyName} />
  ) : (
    <span className="da-logo-initials">{(institution.companyName || "?").slice(0, 1)}</span>
  );

  if (slim) {
    return (
      <div className="da-header-band-slim">
        <div className="da-header-left">
          <div className="da-logo-tile">{logo}</div>
          <p className="da-company-name">{institution.companyName}</p>
        </div>
        <div className="da-header-right">
          <p className="da-doc-title">{docTitle} · {docNumber}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="da-header-band">
      <div className="da-header-left">
        <div className="da-logo-tile">{logo}</div>
        <div>
          <p className="da-company-name">{institution.companyName}</p>
          {institution.companyGstin && <p className="da-company-gstin">{institution.companyGstin}</p>}
        </div>
      </div>
      <div className="da-header-right">
        <p className="da-doc-title">{docTitle}</p>
        <p className="da-doc-number">{docNumber}</p>
      </div>
    </div>
  );
}

function InfoStrip({ config }) {
  const { party = {}, docNumber } = config;
  return (
    <div className="da-info-strip">
      <span><strong>Bill To:</strong> {party.billTo?.name}</span>
      {party.meta?.[0] && <span>{party.meta[0].label}: <strong>{party.meta[0].value}</strong></span>}
      <span>{docNumber}</span>
    </div>
  );
}

function PartyBlock({ config }) {
  const { party = {} } = config;
  return (
    <div className="da-party-block">
      <div className="da-party-col">
        <p className="da-label">{party.billToLabel || "Bill To"}</p>
        <p className="da-party-name">{party.billTo?.name}</p>
        {(party.billTo?.lines || []).map((l, i) => <div key={i} className="da-party-line">{l}</div>)}
        {party.shipTo && (
          <>
            <p className="da-label" style={{ marginTop: 10 }}>Ship To</p>
            {(party.shipTo.lines || []).map((l, i) => <div key={i} className="da-party-line">{l}</div>)}
          </>
        )}
      </div>
      <div className="da-party-col">
        <p className="da-label">{party.fromLabel || "From"}</p>
        <p className="da-party-name">{party.from?.name}</p>
        {(party.from?.lines || []).map((l, i) => <div key={i} className="da-party-line">{l}</div>)}
      </div>
      <div className="da-party-col">
        <p className="da-label">{party.metaLabel || "Details"}</p>
        {(party.meta || []).map((m, i) => (
          <div key={i} className="da-meta-row"><span>{m.label}</span><span>{m.value}</span></div>
        ))}
      </div>
    </div>
  );
}

// bankDetails comes from the Chart of Accounts bank account marked
// "primary" (see apiservice/accounts.js's getPrimaryBankAccountApi) --
// mirrors backend/src/pdfTemplates/designA/designACore.js's
// renderBankDetailsHtml exactly. Only shows the fields that account
// actually has (IFSC vs. SWIFT/IBAN varies by bank/country).
function BankDetails({ bankDetails }) {
  const lines = [];
  if (bankDetails.bankName) {
    lines.push(
      `Bank: ${bankDetails.bankName}${bankDetails.accountNameAsPerBank ? ` · A/c Name: ${bankDetails.accountNameAsPerBank}` : ""}`
    );
  }
  if (bankDetails.accountNo) {
    lines.push(`A/c No: ${bankDetails.accountNo}${bankDetails.branch ? ` · Branch: ${bankDetails.branch}` : ""}`);
  }
  const codes = [
    bankDetails.ifsc ? `IFSC: ${bankDetails.ifsc}` : null,
    bankDetails.swiftCode ? `SWIFT: ${bankDetails.swiftCode}` : null,
    bankDetails.ibanNumber ? `IBAN: ${bankDetails.ibanNumber}` : null,
  ].filter(Boolean);
  if (codes.length) lines.push(codes.join(" · "));

  if (!lines.length) return null;

  return (
    <div className="da-bank-details">
      <p className="da-label">Bank Details</p>
      {lines.map((l, i) => <div key={i}>{l}</div>)}
    </div>
  );
}

function TotalsAndFooterSection({ config }) {
  const { totalsRows = [], grandTotalLabel, grandTotalValue, paidValue, dueValue, amountInWordsText, bankDetails, terms, signatory = {}, bonusNote, taxSummary } = config;

  return (
    <>
      {bonusNote && <p className="da-bonus-note">{bonusNote}</p>}

      {taxSummary?.rows?.length > 0 && (
        <div className="da-tax-summary">
          <p className="da-label">Tax Summary</p>
          <table>
            <thead>
              <tr>
                <th>GST Rate</th><th>Taxable</th>
                {taxSummary.mode === "igst" ? <th>IGST</th> : <><th>CGST</th><th>SGST</th></>}
              </tr>
            </thead>
            <tbody>
              {taxSummary.rows.map((r, i) => (
                <tr key={i}>
                  <td>{r.rate}</td><td className="da-numeric">{r.taxable}</td>
                  {taxSummary.mode === "igst" ? (
                    <td className="da-numeric">{r.igst}</td>
                  ) : (
                    <><td className="da-numeric">{r.cgst}</td><td className="da-numeric">{r.sgst}</td></>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="da-totals-area">
        <div className="da-totals-left">
          {amountInWordsText && <p className="da-amount-words">In Words: {amountInWordsText}</p>}
          {bankDetails && <BankDetails bankDetails={bankDetails} />}
        </div>
        <div className="da-totals-right">
          {totalsRows.map((r, i) => (
            <div key={i} className={`da-totals-row ${r.credit ? "da-credit" : ""}`}>
              <span>{r.label}</span><span className="da-numeric">{r.value}</span>
            </div>
          ))}
          <div className="da-grand-bar"><span>{grandTotalLabel || "Grand Total"}</span><span className="da-numeric">{grandTotalValue}</span></div>
          {paidValue != null && <div className="da-totals-row"><span>Paid</span><span className="da-numeric">{paidValue}</span></div>}
          {dueValue != null && <div className="da-totals-row da-due-row"><span>Invoice Due</span><span className="da-numeric">{dueValue}</span></div>}
        </div>
      </div>

      <div className="da-terms-sign">
        <div className="da-terms">
          <p className="da-label">Terms &amp; Notes</p>
          <div>{terms}</div>
        </div>
        <div className="da-signature-row">
          {signatory.stampUrl && (
            <div className="da-stamp-block"><img src={signatory.stampUrl} alt="Stamp" /></div>
          )}
          <div className="da-signature-block">
            <p className="da-for-company">For {signatory.companyName}</p>
            {signatory.signatureUrl ? <img src={signatory.signatureUrl} alt="Signature" /> : <div style={{ height: 34 }} />}
            <div className="da-sign-rule">Authorised Signatory</div>
          </div>
        </div>
      </div>
    </>
  );
}

function FooterBar({ pageNo, totalPages }) {
  return (
    <div className="da-footer-bar">
      <span>This is a computer-generated document.</span>
      <span>Page {pageNo} of {totalPages}</span>
    </div>
  );
}

export default function VoucherBodyDesignA({ config }) {
  if (!config) return null;
  const columns = config.columns || [];
  const rowBudgets = { ...DEFAULT_ROW_BUDGETS, ...(config.rowBudgets || {}) };

  const { pages, totalPages } = paginateDesignARows(config.rows || [], {
    rowBudgets,
    amountFn: config.amountFn,
  });

  return (
    <div className="da-root">
      {pages.map((page) => {
        const isFirstOrSingle = page.type === "single" || page.type === "first";
        const isLastOrSingle = page.type === "single" || page.type === "last";

        return (
          <div className="da-page" key={page.pageNo}>
            <HeaderBand config={config} slim={false} />
            {!isFirstOrSingle && <InfoStrip config={config} />}
            <div className="da-content">
              {isFirstOrSingle && <PartyBlock config={config} />}
              <table className={["da-table", config.tableClassName].filter(Boolean).join(" ")}>
                <thead>
                  <tr>{columns.map((c, i) => <th key={i} style={{ textAlign: c.align || "left", width: c.width }}>{c.label}</th>)}</tr>
                </thead>
                <tbody>
                  {page.broughtForwardTotal != null && (
                    <tr className="da-bring-row">
                      <td colSpan={columns.length}>
                        Brought forward from page {page.pageNo - 1} — running total {page.broughtForwardTotal}
                      </td>
                    </tr>
                  )}
                  {page.rows.map((r, i) => (
                    <tr key={i} className={r.isBonus ? "bonus-row" : ""}>
                      {r.cells.map((c, j) => {
                        const col = columns[j] || {};
                        return (
                          <td key={j} className={col.cellClassName} style={{ textAlign: col.align || "left" }}>{c}</td>
                        );
                      })}
                    </tr>
                  ))}
                  {page.carriedForwardTotal != null && (
                    <tr className="da-carry-row">
                      <td colSpan={columns.length - 1}>Carried forward to page {page.pageNo + 1} (before tax)</td>
                      <td className="da-amount-cell da-numeric">{page.carriedForwardTotal}</td>
                    </tr>
                  )}
                </tbody>
              </table>
              {isLastOrSingle && (
                <div className="da-row-count-line">
                  <span>Total items: {config.totalItems ?? ""}</span>
                  <span>Total qty: {config.totalQty ?? ""}</span>
                </div>
              )}
              <div className="da-spacer" />
              {isLastOrSingle && <TotalsAndFooterSection config={config} />}
            </div>
            <FooterBar pageNo={page.pageNo} totalPages={totalPages} />
          </div>
        );
      })}
    </div>
  );
}
